import React, { useEffect, useState, useRef } from 'react';
import {
  generatePath,
  Link,
  Outlet,
  useLocation,
  useParams,
  useNavigate,
} from 'react-router-dom';
import useApiContext from 'hooks/useApiContext';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import useRepo from 'hooks/useRepo';
import useModalContext from 'hooks/useModalContext';
import { A, M, T } from 'TOMA';
import { Switch } from 'components/Switch';
import { InputPhone, InputText, InputCheckbox } from 'form';
import { eRoles } from 'interfaces/Roles';
import s from './EventRegistration.module.scss';
import Exhibitor from 'pages/Exhibitor';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import IconButton from '@mui/material/IconButton';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import Typography from '@mui/material/Typography';
import EventProductCard from './EventProductCard.page';
import EventShowroomProductCard from './EventShowroomProductCard.page';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { InputAdornment, TextField } from '@mui/material';
import { DateRange } from '@mui/icons-material';
import CreateProductForm from '../ProductList/CreateProductForm.page';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import CreateIcon from '@mui/icons-material/Create';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import ExhibitorForEvent from '../Exhibitor/ExhibitorForEvent';
import routes from '../../router/routes';
import { toast } from 'react-toastify';
import event from 'i18n/en/event';
import { use } from 'i18next';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { parse } from 'path';

export interface iForm {
  first_name: string;
  last_name: string;
  telephone: string;
  email: string;
  contract_position: string;
  nom_societe?: string;
  trade_name_local?: string;
  visibility: number;
  individual?: number;
  company?: number;
  image1: string;
  image2: string;
  collectif: string;
}

function EventRegistration(): JSX.Element {
  const childFormRef = useRef<{
    submitForm: () => void;
    validate: () => Promise<boolean>;
  }>(null);
  const childFormValidRef = useRef<{}>(null);
  const handleParentSubmit = async (e: any): Promise<boolean> => {
    e.preventDefault();

    if (childFormRef.current) {
      const valid = await childFormRef.current.validate();
      if (!valid) return false; // stop submit if child invalid

      await childFormRef.current.submitForm();
      return true;
    }

    return true;
  };
  const {
    userRepository,
    codificationRepository,
    exhibitorEventRepository,
    productRepository,
  } = useApiContext();
  const { exhibitorEventId, exhibitorId, eventId } = useParams();
  const [fireFetchProduct, setFireFetchProduct] = useState(0);
  const [fireFetchShowroomProduct, setFireFetchShowroomProduct] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { setModalContent, setModalVisible } = useModalContext();
  const { t } = useTranslation();
  const user = userRepository.getUser();
  const { response, loading, error } = useRepo(async () => {
    if (!exhibitorId) {
      return await exhibitorEventRepository.getExhibitorEvent(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        parseInt(exhibitorEventId!)
      );
    } else {
      return await exhibitorEventRepository.getExhibitorEventAdmin(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        parseInt(exhibitorId),
        // safely provide eventId as number or undefined
        eventId ? parseInt(eventId) : undefined
      );
    }
  });

  const basePath = routes.event.path.split('/').filter(Boolean)[0];
  console.log('BasePath:', basePath);

  const [showroomEnabled, setShowroomEnabled] = useState(false);

  // Thematic selections are stored product-wise: each showroom product id maps to its own
  // [thematic_1, thematic_2, thematic_3] selection.
  const [thematicsByProduct, setThematicsByProduct] = useState<
    Record<string, string[]>
  >({});
  // Legacy data recorded before thematics were product-specific (product_id was null).
  // Kept only to display previously saved choices for the first showroom product.
  const [legacyThematics, setLegacyThematics] = useState<string[]>([]);
  const { pathname } = useLocation();
  const { eventRepository } = useApiContext();

  // Replace with an existing method or implement fetchEventExtraFields in iEventRepository
  // Example: using getEventExtraFields if it exists
  const { response: eventExtraFields } = useRepo(async () => {
    if (response?.event?.id) {
      const eventExtra = await eventRepository.getEventById(
        String(response?.event?.id)
      );
      return eventExtra.event_extra_fields;
    }
    return null;
  }, [exhibitorEventId, response?.event?.id, showroomEnabled]);

  const { response: products } = useRepo(async () => {
    const id = exhibitorEventId
      ? parseInt(exhibitorEventId)
      : response?.exhibitorEvent?.id;
    if (id) {
      return await exhibitorEventRepository.getExhibitorEventProduct(id);
    }
    return null;
  }, [fireFetchProduct, response]);

  const { response: showroom_products } = useRepo(async () => {
    const id = exhibitorEventId
      ? parseInt(exhibitorEventId)
      : response?.exhibitorEvent?.id;
    if (id) {
      return await exhibitorEventRepository.getExhibitorEventShowroomProduct(
        id
      );
    }
    return null;
  }, [fireFetchShowroomProduct, response]);

  // Number of thematic choices allowed for this event (configured by the admin, default 3).
  const thematicCount = Math.min(
    12,
    Math.max(1, Number(response?.event?.thematic_selection_count) || 3)
  );

  // Thématiques can be switched off per event by the admin (enabled by default).
  const thematicsEnabled =
    response?.event?.thematic_enabled === undefined ||
    response?.event?.thematic_enabled === null ||
    !!Number(response?.event?.thematic_enabled);

  // Max number of e-catalogue products configured by the admin (default 10).
  const catalogLimit = Math.max(
    1,
    Number(response?.event?.catalog_products_limit) || 10
  );
  const catalogLimitReached = (products?.length ?? 0) >= catalogLimit;

  const getThematicValue = (productId: string, index: number): string => {
    const values = thematicsByProduct[productId];
    if (values && values.length > 0) {
      return values[index] ?? '';
    }
    // Fall back to legacy (pre-product) data for the first showroom product only.
    if (
      legacyThematics.length > 0 &&
      showroom_products &&
      showroom_products[0] &&
      String(showroom_products[0].product_id) === productId
    ) {
      return legacyThematics[index] ?? '';
    }
    return '';
  };

  const handleThematicChange = (
    productId: string,
    index: number,
    value: string
  ): void => {
    const current = Array.from({ length: thematicCount }, (_, i) =>
      getThematicValue(productId, i)
    );
    current[index] = value;

    setThematicsByProduct((prev) => ({ ...prev, [productId]: current }));

    const id = exhibitorEventId ?? String(response?.exhibitorEvent?.id ?? '');
    if (!id) {
      console.error('Exhibitor Event ID is missing');
      return;
    }
    exhibitorEventRepository.addExhibitorEventExtraField(parseInt(id), {
      product_id: parseInt(productId),
      extra_fields: current,
    });
  };

  const { response: collectif } = useRepo(
    async () => await codificationRepository.get('collectif')
  );
  const {
    response: productLists,
    reload: reloadProducts,
    loading: loadingProducts,
  } = useRepo(async () => await productRepository.getProducts('', exhibitorId));
  const [isSend, setIsSendSent] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [successForm, setSuccessForm] = useState(false);
  const [errorForm, setErrorForm] = useState<string>();
  const [answers, setAnswers] = useState<{ [key: string]: string | number }>(
    {}
  );
  const questionField = (questionId: number | string): string =>
    `qa_${questionId}`;
  // only questions flagged as mandatory are shown to exhibitors
  const visibleQuestions = (response?.questions ?? []).filter(
    (item: any) => !!item.required
  );
  const generateQuestionBlock = (): any => {
    return visibleQuestions.map((item: any) => {
      const fieldName = questionField(item.id);
      return (
        <React.Fragment key={fieldName}>
          <Controller
            // @ts-expect-error
            name={fieldName}
            control={control}
            render={({ field }) => (
              <T.BlockResponsive>
                <A.InputSelect
                  {...field}
                  name={fieldName}
                  required={!!item.required}
                  label={item.question}
                  disabled={response?.exhibitorEvent.locked}
                  options={(item.answers ?? []).map(
                    (answer: any, index: number) => ({
                      label: answer,
                      value: `answer_${index + 1}`,
                    })
                  )}
                />
              </T.BlockResponsive>
            )}
          />
        </React.Fragment>
      );
    });
  };
  const schema = yup.object({
    // first_name: yup.string().max(255).required(t('event:required')).nullable(),
    // last_name: yup.string().max(255).required(t('event:required')).nullable(),
    // telephone: yup.string().max(255).required(t('event:required')).nullable(),
    // email: yup
    //   .string()
    //   .max(255)
    //   .email('Please enter a valid email')
    //   .required(t('event:required'))
    //   .nullable(),
    ...Object.fromEntries(
      visibleQuestions.map((item: any) => [
        questionField(item.id),
        item.required
          ? yup.string().required(t('event:required'))
          : yup.string().nullable(),
      ])
    ),
  });

  const form = useForm<iForm>({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const {
    formState: { errors },
  } = form;
  const { handleSubmit, watch, control, reset, setValue, getValues } = form;
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const handleCheckboxChange = (value: any): any => {
    if (value === 'individual') {
      setValue('collectif', '');
    }
    setSelectedOption(value);
  };
  useEffect(() => {
    setShowroomEnabled(!!response?.exhibitorEvent?.showroom);
    if (response?.exhibitorEvent) {
      const savedAnswers: { [key: string]: string } = {};
      Object.entries(response?.question_answers ?? {}).forEach(
        ([questionId, answerIndex]) => {
          savedAnswers[questionField(questionId)] = `answer_${String(
            answerIndex
          )}`;
        }
      );
      reset({
        ...response?.exhibitorEvent,
        ...savedAnswers,
        nom_societe: response?.exhibitorEvent?.nom_societe ?? '',
        trade_name_local: response?.exhibitorEvent?.trade_name_local ?? '',
        ...products,
      });
      if (response?.exhibitorEvent?.collectif) {
        setSelectedOption('company');
      } else {
        setSelectedOption('individual');
      }
    }
  }, [response, products]);
  const navigate = useNavigate();
  const handleRedirect = (): any => {
    const path = generatePath(`${routes.event.path}`, { eventId });
    navigate(path, {
      replace: true,
      state: { reloadKey: Date.now() },
    });
    setReload((e) => e + 1);
  };
  const handleRedirectEvents = (): any => {
    const path = generatePath(`${routes.eventlist.path}`, {});
    navigate(path, {
      replace: true,
      state: { reloadKey: Date.now() },
    });
  };
  function onSubmit(data: iForm, is_sent: boolean = false): void {
    setLoadingForm(true);
    if (!event) {
      setErrorForm('Erreur inconnue...');
      setLoadingForm(false);
      return;
    }
    const payload = {
      first_name: data.first_name,
      last_name: data.last_name,
      visibility: data.visibility,
      telephone: data.telephone || '',
      email: data.email,
      contract_position: data.contract_position,
      nom_societe: data.nom_societe?.trim() ?? '',
      trade_name_local: data.trade_name_local?.trim() ?? '',
      is_send: is_sent,
      question_answers: Object.fromEntries(
        visibleQuestions.map((item: any) => {
          const value = (data as any)[questionField(item.id)] as
            | string
            | undefined;
          return [item.id, value ? Number(value.replace('answer_', '')) : null];
        })
      ),
      collectif: data.collectif,
      showroom_products_count: showroom_products?.length ?? 0,
    };

    Promise.all([
      exhibitorEventRepository
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        // @ts-expect-error
        .mergeExhibitorEvent(parseInt(response?.exhibitorEvent?.id), payload)
        .finally(() => setLoadingForm(false)),
    ])
      .then(() => {
        setSuccessForm(true);
        setErrorForm('');
      })
      .catch((err: Error) => {
        toast(err.message, { type: 'error' });
        setErrorForm(err.message);
      })
      .finally(() => {
        setLoadingForm(false);
        if (is_sent) {
          toast(t('event:exhibitror_successfilly_updated'), {
            type: 'success',
          });
          setIsPopupOpen(true);
        } else {
          toast('Form submitted successfully!', { type: 'success' });
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        user?.role === 'ADMIN' ? handleRedirect() : '';
      });
  }

  const mediaList: Array<
    'media_1' | 'media_2' | 'media_3' | 'media_4' | 'media_5'
  > = ['media_1', 'media_2', 'media_3', 'media_4', 'media_5'];

  const [selectedYear, setSelectedYear] = React.useState('');

  const handleChange = (event: SelectChangeEvent): void => {
    const selectedValue = event.target.value;
    setSelectedYear(selectedValue);
    // if (selectedValue) {
    //   handleAddProduct(event);
    // }
  };

  const [selectedProduct, setSelectedProduct] = useState<any>('');
  const [selectedProductIndex, setSelectedProductIndex] = useState<any>('');

  const handleSelectedProduct = (event: SelectChangeEvent): void => {
    const product = productLists?.products?.find(
      (item, index) => index === Number(event.target.value)
    );
    setSelectedProduct(product ?? null);
    setSelectedProductIndex(event.target.value);
    if (product) {
      const productsArr = Array.isArray(product.products)
        ? product.products
        : [];
      if (productsArr.length > 1) {
        // Multiple vintages -> clear year so user must choose
        setSelectedYear('');
      }
      if (productsArr && productsArr.length === 1) {
        // Single vintage -> auto-select it (this will trigger the effect that adds the product)
        setSelectedYear(String(productsArr[0].id ?? ''));
      }
    } else {
      setSelectedYear('');
    }
  };

  useEffect(() => {
    if (selectedYear || (selectedYear === '' && selectedProduct)) {
      handleAddProduct();
    }
  }, [selectedYear, selectedProduct]);

  const handleAddProduct = async (): Promise<boolean> => {
    if (catalogLimitReached) {
      return false;
    }
    if (selectedYear !== '' || (selectedYear === '' && selectedProduct)) {
      if (parseInt(selectedYear) > 0) {
        await exhibitorEventRepository.addExhibitorProductEvent(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          parseInt(String(response?.exhibitorEvent.id) ?? exhibitorEventId!),
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          parseInt(selectedYear)
        );
        setSelectedYear('');
        setSelectedProduct(null);
        setSelectedProductIndex('');
        setFireFetchProduct(fireFetchProduct + 1);
      }
    } else {
      setShowAlert(true);
      setAlertMessage(
        'Please select year for product and then click on add button.'
      );
    }
    return false;
  };

  const [selectedShowroomProduct, setSelectedShowroomProduct] =
    useState<any>('');
  const [selectedShowroomProductIndex, setSelectedShowroomProductIndex] =
    useState<any>('');

  const [selectedShowroomYear, setSelectedShowroomYear] = React.useState('');

  const handleShowroomChange = (event: SelectChangeEvent): void => {
    setSelectedShowroomYear(event.target.value);
  };

  useEffect(() => {
    if (
      selectedShowroomYear ||
      (selectedShowroomYear === '' && selectedShowroomProduct)
    ) {
      handleAddShowroomProduct();
    }
  }, [selectedShowroomYear, selectedShowroomProduct]);

  // Load saved thematic selections and group them by product for the current exhibitor/event.
  useEffect(() => {
    const id = exhibitorEventId ?? String(response?.exhibitorEvent?.id ?? '');
    if (!id) return;
    exhibitorEventRepository
      .getExhibitorEventExtraFields(parseInt(id))
      .then((data) => {
        if (!Array.isArray(data)) return;
        const grouped: Record<string, string[]> = {};
        const legacy: string[] = [];
        data.forEach((field: any) => {
          if (field.product_id) {
            const key = String(field.product_id);
            grouped[key] = [...(grouped[key] ?? []), field.field_value];
          } else {
            legacy.push(field.field_value);
          }
        });
        setThematicsByProduct(grouped);
        setLegacyThematics(legacy);
      });
  }, [exhibitorEventId, response?.exhibitorEvent?.id, showroomEnabled]);

  const handleSelectedShowroomProduct = (event: SelectChangeEvent): void => {
    const product = productLists?.products?.find(
      (item, index) => index === Number(event.target.value)
    );
    setSelectedShowroomProduct(product ?? null);
    setSelectedShowroomProductIndex(event.target.value);
    if (product) {
      const showroomProductsArr = Array.isArray(product.products)
        ? product.products
        : [];

      if (showroomProductsArr && showroomProductsArr.length === 1) {
        // Single vintage -> auto-select it (this will trigger the effect that adds the product)
        setSelectedShowroomYear(String(showroomProductsArr[0].id));
      }
    } else {
      handleAddShowroomProduct();
    }
  };

  const handleAddShowroomProduct = async (): Promise<boolean> => {
    if (
      selectedShowroomYear !== '' ||
      (selectedShowroomYear === '' && selectedShowroomProduct)
    ) {
      console.log('Selected Showroom Year:', selectedShowroomYear);
      if (parseInt(selectedShowroomYear)) {
        await exhibitorEventRepository.addExhibitorShowroomProductEvent(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          parseInt(String(response?.exhibitorEvent.id) ?? exhibitorEventId!),
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          parseInt(selectedShowroomYear)
        );
        setSelectedShowroomYear('');
        setSelectedShowroomProduct(null);
        setSelectedShowroomProductIndex('');
        setFireFetchShowroomProduct(fireFetchShowroomProduct + 1);
      }
    } else {
      setShowAlert(true);
      setAlertMessage(
        'Please select year for product and then click on add button.'
      );
    }
    return false;
  };

  const [showNewProductForm, setShowNewProductForm] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [hideInformationAboutCompany, setHideInformationAboutCompany] =
    useState(false);
  const handleClose = (): void => {
    setShowNewProductForm(false);
  };
  const [reload, setReload] = useState<number>(0);
  const hideAlert = (): void => {
    setShowAlert(false);
    setAlertMessage('');
  };
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const [productListState, setProductlistState] = useState(productLists);
  const newProductId = queryParams.get('newProductId');
  useEffect(() => {
    // Define a function to reload products and handle the state update
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const fetchProducts = async () => {
      // If we're already loading, don't reload again
      if (loadingProducts) return;

      // Trigger reload of products
      await reloadProducts();
    };

    // Only call fetchProducts when newProductId changes
    if (newProductId && !loadingProducts) {
      fetchProducts();
    }
  }, [newProductId]); // Trigger only when newProductId changes

  useEffect(() => {
    // Once loading is done, update the state
    if (!loadingProducts) {
      const product = productLists?.products?.find(
        (item) => item.id === Number(newProductId)
      );
      const productIndex = productLists?.products?.findIndex(
        (item) => item.id === Number(newProductId)
      );

      setSelectedProduct(product ?? null);
      setSelectedProductIndex(productIndex);
      setSelectedYear('');
    }
  }, [loadingProducts, productLists, newProductId]);

  return (
    <div>
      <div className={pathname.includes('product') ? s.blur : undefined}>
        <T.Container pb="50" pt="20">
          <M.Loading loading={loading} error={error} fullWiewport>
            <T.Block pb="50" mw="large" m="auto">
              <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={showAlert}
                autoHideDuration={6000}
                onClose={hideAlert}
              >
                <Alert
                  onClose={hideAlert}
                  severity="error"
                  variant="filled"
                  sx={{ width: '100%' }}
                >
                  {alertMessage}
                </Alert>
              </Snackbar>
              <T.Block
                pb="50"
                mw="medium"
                m="auto"
                // bg="grey-05"
                p="20"
                borderRadius="7"
              >
                <T.Block>
                  {user?.role !== eRoles.EXHIBITOR && (
                    <T.BlockResponsive
                      // bg="grey-light"
                      pb="20"
                      borderRadius="10"
                    >
                      <A.Title
                        as="h1"
                        fontSize="48"
                        fw="black"
                        className={s.eventInfoTitle}
                      >
                        {t('event:registration')}
                      </A.Title>
                    </T.BlockResponsive>
                  )}
                  <T.Block
                    style={{
                      padding: '20px',
                      borderRadius: '10px',
                      backgroundColor: '#F2F2F2',
                    }}
                  >
                    <T.BlockResponsive>
                      <A.Title
                        className={s.eventInfoTitle}
                        as="h2"
                        fontSize="32"
                      >
                        {t('event:event_information')}
                      </A.Title>
                      <A.Line mb="20" mt="10" />
                      <T.Flex direction="column" gap={2}>
                        <TextField
                          sx={{
                            backgroundColor: '#F2F2F2',
                          }}
                          value={response?.event?.name}
                          label={t('event:name_of_the_event')}
                          variant="outlined"
                          disabled
                        />
                        <TextField
                          sx={{
                            backgroundColor: '#F2F2F2',
                          }}
                          value={
                            response?.event?.date
                              ? new Date(
                                  response.event.date
                                ).toLocaleDateString('en-GB')
                              : ''
                          }
                          label={t('event:date_of_event')}
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <DateRange />
                              </InputAdornment>
                            ),
                          }}
                          disabled
                        />
                        <TextField
                          sx={{
                            backgroundColor: '#F2F2F2',
                          }}
                          value={response?.event?.city}
                          label={t('event:city_of_event')}
                          variant="outlined"
                          disabled
                        />
                        <TextField
                          sx={{
                            backgroundColor: '#F2F2F2',
                          }}
                          value={response?.event?.country}
                          label={t('event:country_of_event')}
                          variant="outlined"
                          disabled
                        />
                        <TextField
                          sx={{
                            backgroundColor: '#F2F2F2',
                          }}
                          value={
                            response?.event?.deadline_date
                              ? new Date(
                                  response.event.deadline_date
                                ).toLocaleDateString('en-GB')
                              : ''
                          }
                          label={t('event:registration_closing_date')}
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <DateRange />
                              </InputAdornment>
                            ),
                          }}
                          disabled
                        />
                      </T.Flex>
                    </T.BlockResponsive>
                    {response?.event?.show_samples_information ? (
                      <T.BlockResponsive>
                        <A.Title
                          className={s.eventInfoTitle}
                          as="h2"
                          fontSize="32"
                          mt="20"
                        >
                          {t('event:sample_informations')}
                        </A.Title>
                        <A.Line mb="20" mt="10" />
                        <T.Flex gap={2}>
                          <TextField
                            sx={{
                              backgroundColor: '#F2F2F2',
                            }}
                            value={response?.event?.number_of_samples}
                            label={t('event:numbers_of_sample_send')}
                            variant="outlined"
                            disabled
                          />
                          <TextField
                            sx={{
                              backgroundColor: '#F2F2F2',
                            }}
                            value={response?.event?.address}
                            label={t('event:address_to_send_semple')}
                            variant="outlined"
                            disabled
                          />
                          <TextField
                            sx={{
                              backgroundColor: '#F2F2F2',
                            }}
                            value={response?.event?.limit_date}
                            label={t('event:limit_date_to_receive_sample')}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <DateRange />
                                </InputAdornment>
                              ),
                            }}
                            disabled
                          />
                        </T.Flex>
                      </T.BlockResponsive>
                    ) : (
                      ''
                    )}
                  </T.Block>
                  {user?.role !== eRoles.EXHIBITOR && (
                    <>
                      <A.Title mt="50" as="h1" fontSize="32" fw="bold" mb="20">
                        <T.Flex halign="center" valign="center" gap={1}>
                          <a
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setHideInformationAboutCompany(
                                (prevState) => !prevState
                              );
                            }}
                          >
                            {t('event:information_de_la_societe')}
                          </a>
                          {hideInformationAboutCompany ? (
                            <ArrowCircleRightOutlinedIcon
                              fontSize="medium"
                              style={{ cursor: 'pointer', fontSize: 28 }}
                              onClick={() => {
                                setHideInformationAboutCompany(() => false);
                              }}
                            />
                          ) : (
                            <ArrowCircleDownIcon
                              fontSize="medium"
                              style={{ cursor: 'pointer', fontSize: 28 }}
                              onClick={() => {
                                setHideInformationAboutCompany(() => true);
                              }}
                            />
                          )}
                        </T.Flex>
                      </A.Title>
                      {/* <A.Line mb="20" /> */}
                      {!hideInformationAboutCompany ? (
                        <ExhibitorForEvent ref={childFormRef} />
                      ) : (
                        ''
                      )}
                    </>
                  )}
                  <form noValidate>
                    {basePath === 'businessfrance' && (
                      <>
                        <A.Title as="h2" mt="20" fontSize="24" fw="bold">
                          {t('exhibitors:my_company_is')}
                        </A.Title>
                        <A.Line mb="20" mt="10" />
                        <T.BlockResponsive>
                          <T.Flex halign="space-between">
                            <A.Title as="h6" fontSize="18" fw="normal">
                              {t('exhibitors:individual')}
                            </A.Title>
                            <T.Block mt="5">
                              <A.InputCheckbox
                                label=""
                                // @ts-expect-error
                                value="individual"
                                checked={selectedOption === 'individual'} // Перевіряємо чи це вибраний елемент
                                onChange={() =>
                                  handleCheckboxChange('individual')
                                }
                                disabled={response?.exhibitorEvent.locked}
                              />
                            </T.Block>
                          </T.Flex>

                          <T.Flex mt="10" halign="space-between">
                            <A.Title as="h6" fontSize="18" fw="normal">
                              Sur un stand collectif régional ou
                              interprofessionnel
                            </A.Title>
                            <T.Block mt="5">
                              <A.InputCheckbox
                                label=""
                                // @ts-expect-error
                                value="company"
                                checked={selectedOption === 'company'} // Перевіряємо чи це вибраний елемент
                                onChange={() => handleCheckboxChange('company')}
                                disabled={response?.exhibitorEvent.locked}
                              />
                            </T.Block>
                          </T.Flex>
                          {selectedOption === 'company' ? (
                            <T.Flex halign="center">
                              <Controller
                                name={`collectif`}
                                control={control}
                                render={({ field }) => (
                                  <A.InputSelect
                                    {...field}
                                    placeholder="Selectionnez..."
                                    name="collectif"
                                    disabled={response?.exhibitorEvent.locked}
                                    options={collectif?.map((item) => {
                                      return {
                                        value: item.code,
                                        label: item.label,
                                      };
                                    })}
                                  />
                                )}
                              />
                            </T.Flex>
                          ) : (
                            ''
                          )}
                        </T.BlockResponsive>
                      </>
                    )}
                    <A.Title as="h2" fontSize="24" mt="50" fw="bold">
                      {t('event:contact_for_this_event')}
                      <br />
                      <A.Text as="span" fontSize="14" fw="normal">
                        ({t('event:informations_visibles_sur_le_e_catalogue')})
                      </A.Text>
                    </A.Title>
                    <A.Line mb="20" mt="10" />
                    <T.BlockResponsive>
                      <InputText
                        label={t('event:sur_name')}
                        name="first_name"
                        type="text"
                        disabled={response?.exhibitorEvent.locked}
                        control={control}
                      />
                      <InputText
                        label={t('event:last_name')}
                        name="last_name"
                        type="text"
                        disabled={response?.exhibitorEvent.locked}
                        control={control}
                      />

                      <InputPhone
                        label={t('event:mobile_phone')}
                        name="telephone"
                        control={control}
                        disabled={response?.exhibitorEvent.locked}
                        placeholder="+33 6 23 45 67 88"
                      />

                      <InputText
                        label={t('event:email')}
                        name="email"
                        type="email"
                        inputMode="email"
                        disabled={response?.exhibitorEvent.locked}
                        control={control}
                      />

                      {/* <InputText
                        label={t('event:contact_position')}
                        name="contract_position"
                        type="text"
                        control={control}
                        disabled={response?.exhibitorEvent.locked}
                        placeholder={t('event:contact_position')}
                      /> */}
                    </T.BlockResponsive>

                    <A.Title as="h2" fontSize="24" mt="50" fw="bold">
                      {t('event:export_profile')}
                    </A.Title>
                    <A.Line mb="20" mt="10" />
                    <T.BlockResponsive>
                      <InputText
                        label={t('event:trade_name')}
                        information={t('event:trade_name_subtitle')}
                        name="nom_societe"
                        type="text"
                        disabled={response?.exhibitorEvent.locked}
                        control={control}
                      />
                      <InputText
                        label={t('event:trade_name_local')}
                        information={t('event:trade_name_local_subtitle')}
                        name="trade_name_local"
                        type="text"
                        disabled={response?.exhibitorEvent.locked}
                        control={control}
                      />
                    </T.BlockResponsive>

                    {visibleQuestions.length > 0 ? (
                      <>
                        <A.Title as="h2" fontSize="24" mt="50" fw="bold">
                          {t('event:questionnaire')}
                        </A.Title>
                        <A.Line mb="20" mt="20" />
                        <style>{`.questionnaire-block label { font-weight: 700; }`}</style>
                        <div className="questionnaire-block">
                          <T.Block>{generateQuestionBlock()}</T.Block>
                        </div>
                      </>
                    ) : null}

                    <A.Title as="h2" fontSize="24" mt="50" fw="black">
                      <T.Block>
                        {t('event:your_products_catalog')}* &nbsp;&nbsp;
                        <br />
                        <A.Text as="span" fontSize="14" fw="normal">
                          {t(
                            'event:possibility_of_presenting_up_to_10_references',
                            { count: catalogLimit }
                          )}
                        </A.Text>
                      </T.Block>
                      <A.Line mb="20" mt="10" />
                      <T.BlockResponsive>
                        <T.Flex mt="20" mb="20" gap={5} direction="column">
                          {products?.map((product, index) => (
                            <EventProductCard
                              key={index}
                              product={product}
                              exhibitorEventId={exhibitorEventId}
                              fireFetchProduct={fireFetchProduct}
                              setFireFetchProduct={setFireFetchProduct}
                              event={response?.event}
                              locked={response?.exhibitorEvent.locked}
                              exhibitorEventAdminId={
                                response?.exhibitorEvent.id
                              }
                              role={user?.role}
                            />
                          ))}
                        </T.Flex>
                        <>
                          {!catalogLimitReached && (
                            <T.Flex mt="20" mb="10" gap={5}>
                              <FormControl
                                sx={{
                                  width: '100%',
                                }}
                              >
                                <InputLabel id="select-product-list">
                                  {t('event:select_product')}
                                </InputLabel>
                                <Select
                                  labelId="select-product-list"
                                  id="select-product"
                                  disabled={response?.exhibitorEvent.locked}
                                  value={selectedProductIndex}
                                  label={t('event:select_product')}
                                  onChange={handleSelectedProduct}
                                >
                                  {productLists?.products &&
                                  productLists.products.length > 0 ? (
                                    productLists.products.map(
                                      (item: any, index: number) => (
                                        <MenuItem key={index} value={index}>
                                          {item.name}
                                        </MenuItem>
                                      )
                                    )
                                  ) : (
                                    <MenuItem value="">
                                      {t('event:no_product_available')}
                                    </MenuItem>
                                  )}
                                </Select>
                              </FormControl>
                              {selectedProduct &&
                                selectedProduct.products[0].type_produit ===
                                  'type_produit:WINE' && (
                                  <FormControl
                                    sx={{
                                      width: '200px',
                                    }}
                                  >
                                    <InputLabel id="simple-select-label-millesime">
                                      Millesime
                                    </InputLabel>
                                    <Select
                                      labelId="simple-select-label-millesime"
                                      id="simple-select-millesime"
                                      value={selectedYear}
                                      label="Millesime"
                                      onChange={handleChange}
                                    >
                                      {selectedProduct.products?.map(
                                        (item: any, index: any) => (
                                          <MenuItem key={index} value={item.id}>
                                            {item.millesime?.replace(
                                              'millesime:',
                                              ''
                                            )}
                                          </MenuItem>
                                        )
                                      )}
                                    </Select>
                                  </FormControl>
                                )}
                            </T.Flex>
                          )}
                          {/* <T.Flex halign="center">
                            <IconButton
                              onClick={handleAddProduct}
                              aria-label="delete"
                              size="large"
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'transparent', // Removes background on hover
                                },
                                '&:focus': {
                                  backgroundColor: 'transparent', // Removes focus (click) background
                                },
                                '&.Mui-focusVisible': {
                                  backgroundColor: 'transparent', // Removes focus background on click
                                },
                                '& .MuiTouchRipple-root': {
                                  display: 'none', // Removes the ripple effect
                                },
                              }}
                            >
                              <img src="/icons/add.svg" />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '16px',
                                  marginLeft: '14px',
                                }}
                              >
                                {t('event:add_product_from_catalog')}
                              </Typography>
                            </IconButton>
                          </T.Flex> */}
                        </>
                      </T.BlockResponsive>
                    </A.Title>
                    {user?.role === eRoles.EXHIBITOR && !catalogLimitReached ? (
                      <T.Flex
                        mt="20"
                        mb="20"
                        halign={'center'}
                        valign={'center'}
                      >
                        <A.Button
                          onClick={() => setShowNewProductForm(true)}
                          disabled={response?.exhibitorEvent.locked}
                          className={s.Button_product_add}
                        >
                          <A.Icon
                            icon="plus"
                            color="white"
                            fontSize="14"
                            mr="1"
                          />
                          {t('event:add_a_new_product')}
                        </A.Button>
                      </T.Flex>
                    ) : null}

                    {/* <A.Line mb="20" mt="20" /> */}

                    {Boolean(response?.event?.showroom) && (
                      <A.Title as="h2" fontSize="24" mt="50" fw="black">
                        <A.Title as="h2" fontSize="32" fw="bold" mb="20">
                          <div style={{ textAlign: 'center' }}>
                            {t('event:animation')}
                          </div>
                        </A.Title>
                        <T.Flex gap={1} mb="10">
                          <div>
                            {response?.event?.animation_title ??
                              'Le Hub #BonjourProWein'}{' '}
                            &nbsp;
                          </div>
                          <T.Flex gap={1} valign="center">
                            <Switch
                              name="showroom"
                              key={1}
                              disable={response?.exhibitorEvent.locked}
                              checked={showroomEnabled}
                              onChange={async (
                                e: boolean
                              ): Promise<boolean> => {
                                if (!e) {
                                  setModalContent(
                                    <div
                                      style={{
                                        width: '600px',
                                        maxWidth: '90vw',
                                      }}
                                    >
                                      <A.Text
                                        fontSize="22"
                                        fw="bold"
                                        mb="20"
                                        textAlign="center"
                                      >
                                        {t('users:confirm_remove_product')}
                                      </A.Text>

                                      <T.Flex gap={2} valign="center" mb="-15">
                                        <A.Button
                                          bg="alert"
                                          onClick={async () => {
                                            // Confirm → actually turn OFF
                                            setShowroomEnabled(false);

                                            const data = { showroom: false };

                                            await exhibitorEventRepository.mergeExhibitorEvent(
                                              parseInt(
                                                exhibitorEventId ??
                                                  String(
                                                    response?.exhibitorEvent?.id
                                                  )
                                              ),
                                              data
                                            );

                                            setFireFetchShowroomProduct(
                                              fireFetchShowroomProduct + 1
                                            );

                                            setModalVisible(false);
                                          }}
                                        >
                                          {t('users:confirm')}
                                        </A.Button>

                                        <A.Button
                                          appearance="link"
                                          color="grey-80"
                                          textDecoration="underline"
                                          onClick={() => {
                                            setShowroomEnabled(true);
                                            setModalVisible(false);
                                          }}
                                        >
                                          {t('users:cancel')}
                                        </A.Button>
                                      </T.Flex>
                                    </div>
                                  );

                                  setModalVisible(true);

                                  throw new Error('User cancelled');
                                }

                                setShowroomEnabled(true);

                                const data = { showroom: true };

                                await exhibitorEventRepository.mergeExhibitorEvent(
                                  parseInt(
                                    exhibitorEventId ??
                                      String(response?.exhibitorEvent?.id)
                                  ),
                                  data
                                );

                                return true;
                              }}
                            />
                          </T.Flex>
                          <A.Text fontSize="14" fw="normal" mt="auto" mb="auto">
                            {t('exhibitors:participation')}
                          </A.Text>
                        </T.Flex>
                        {response?.event?.animation_description ? (
                          <div style={{ whiteSpace: 'pre-line' }}>
                            <A.Text fontSize="14" fw="normal">
                              {response.event.animation_description}
                            </A.Text>
                          </div>
                        ) : (
                          <A.Text fontSize="14" fw="normal">
                            En sélectionnant cette option, je confirme mon
                            intention de présenter un produit phare lors du
                            salon et m&apos;engage à fournir, le jour de
                            l&apos;installation (samedi 14 mars 2026), 6
                            échantillons (2 pour les spiritueux) pour les tables
                            de dégustation libre.
                          </A.Text>
                        )}
                        <A.Line mb="20" mt="20" />
                        <T.BlockResponsive>
                          <T.Flex mt="20" mb="20" gap={5} direction="column">
                            {showroomEnabled &&
                              showroom_products?.map((product, index) => {
                                const productId = String(product.product_id);
                                const value1 = getThematicValue(productId, 0);
                                const thematicValues = Array.from(
                                  { length: thematicCount },
                                  (_, i) => getThematicValue(productId, i)
                                );
                                const isLocked =
                                  (response?.exhibitorEvent.locked ?? false) ||
                                  !showroomEnabled;
                                const showThematic1Error =
                                  isSend &&
                                  showroomEnabled &&
                                  thematicsEnabled &&
                                  !value1;

                                return (
                                  <T.Block key={index}>
                                    <EventShowroomProductCard
                                      product={product}
                                      exhibitorEventId={
                                        exhibitorEventId ??
                                        response?.exhibitorEvent.id
                                      }
                                      fireFetchProduct={
                                        fireFetchShowroomProduct
                                      }
                                      setFireFetchProduct={
                                        setFireFetchShowroomProduct
                                      }
                                      event={response?.event}
                                      locked={isLocked}
                                    />
                                    {thematicsEnabled &&
                                    (user?.role === eRoles.EXHIBITOR ||
                                      user?.role === eRoles.ADMIN) ? (
                                      <>
                                        {showroomEnabled &&
                                        (user?.role === eRoles.EXHIBITOR ||
                                          user?.role === eRoles.ADMIN) ? (
                                          <>
                                            <T.Flex mt="10" mb="10">
                                              <A.Text
                                                as="div"
                                                fontSize="16"
                                                fw="bold"
                                              >
                                                {response?.event
                                                  ?.thematic_title ? (
                                                  response.event.thematic_title
                                                ) : (
                                                  <>
                                                    {`Je sélectionne ${thematicCount} ${
                                                      thematicCount > 1
                                                        ? 'thématiques'
                                                        : 'thématique'
                                                    }`}{' '}
                                                    pour ma référence présentée*
                                                  </>
                                                )}
                                              </A.Text>
                                            </T.Flex>
                                            <T.Block>
                                              <A.Text fontSize="14" fw="normal">
                                                {response?.event
                                                  ?.thematic_description ? (
                                                  <span
                                                    style={{
                                                      whiteSpace: 'pre-line',
                                                    }}
                                                  >
                                                    {
                                                      response.event
                                                        .thematic_description
                                                    }
                                                  </span>
                                                ) : (
                                                  <>
                                                    Selon les inscriptions
                                                    reçues, et pour garantir un
                                                    équilibre du nombre
                                                    d’échantillons par
                                                    thématique, Business France
                                                    effectuera un justement de
                                                    la sélection en tenant
                                                    compte de{' '}
                                                    {thematicCount === 3
                                                      ? 'vos trois choix'
                                                      : thematicCount === 1
                                                      ? 'votre choix'
                                                      : `vos ${thematicCount} choix`}
                                                    .
                                                  </>
                                                )}
                                              </A.Text>
                                            </T.Block>
                                          </>
                                        ) : null}
                                        {Array.from(
                                          { length: thematicCount },
                                          (_, k) => k
                                        ).map((k) => {
                                          if (
                                            k > 0 &&
                                            !(
                                              Array.isArray(eventExtraFields) &&
                                              eventExtraFields.length > k
                                            )
                                          ) {
                                            return null;
                                          }
                                          const selfValue = thematicValues[k];
                                          const labelText = `${t(
                                            'event:thematic_choice'
                                          )}${k + 1}${k === 0 ? '*' : ''}`;
                                          const selectId = `select-thematic-${
                                            k + 1
                                          }-${productId}`;
                                          return (
                                            <T.Flex
                                              key={k}
                                              mt="10"
                                              mb={
                                                k === thematicCount - 1 && k > 0
                                                  ? '20'
                                                  : '10'
                                              }
                                              gap={1}
                                            >
                                              <FormControl
                                                sx={{ width: '100%' }}
                                                disabled={isLocked}
                                                error={
                                                  k === 0
                                                    ? showThematic1Error
                                                    : undefined
                                                }
                                              >
                                                <InputLabel id={selectId}>
                                                  {labelText}
                                                </InputLabel>
                                                <Select
                                                  labelId={selectId}
                                                  id={selectId}
                                                  value={selfValue}
                                                  label={labelText}
                                                  onChange={(e) =>
                                                    handleThematicChange(
                                                      productId,
                                                      k,
                                                      e.target.value
                                                    )
                                                  }
                                                  required={k === 0}
                                                >
                                                  {Array.isArray(
                                                    eventExtraFields
                                                  ) &&
                                                  eventExtraFields.length >
                                                    0 ? (
                                                    eventExtraFields.map(
                                                      (
                                                        item: any,
                                                        idx: number
                                                      ) =>
                                                        !thematicValues.some(
                                                          (v, j) =>
                                                            j !== k &&
                                                            v ===
                                                              item.field_value
                                                        ) && (
                                                          <MenuItem
                                                            key={idx}
                                                            value={
                                                              item.field_value
                                                            }
                                                          >
                                                            {item.field_value}
                                                          </MenuItem>
                                                        )
                                                    )
                                                  ) : (
                                                    <MenuItem value="">
                                                      {t(
                                                        'event:no_thematic_found'
                                                      )}
                                                    </MenuItem>
                                                  )}
                                                </Select>
                                                {k === 0 ? (
                                                  <Typography
                                                    variant="caption"
                                                    color="error"
                                                  >
                                                    {showThematic1Error
                                                      ? t('event:required')
                                                      : ''}
                                                  </Typography>
                                                ) : null}
                                              </FormControl>
                                            </T.Flex>
                                          );
                                        })}
                                      </>
                                    ) : null}
                                  </T.Block>
                                );
                              })}
                            {showroom_products &&
                              response?.event &&
                              showroom_products?.length <
                                response.event.number_of_products &&
                              showroomEnabled && (
                                <T.Flex mt="20" mb="10" gap={5}>
                                  <FormControl
                                    sx={{
                                      width: '100%',
                                    }}
                                  >
                                    <InputLabel id="select-product-list">
                                      {t('event:select_product')}
                                    </InputLabel>
                                    <Select
                                      labelId="select-product-list"
                                      id="select-product"
                                      value={selectedShowroomProductIndex}
                                      disabled={response?.exhibitorEvent.locked}
                                      label={t('event:select_product')}
                                      onChange={handleSelectedShowroomProduct}
                                    >
                                      {productLists &&
                                      productLists.products.length > 0 ? (
                                        productLists.products.map(
                                          (item: any, index: number) => (
                                            <MenuItem key={index} value={index}>
                                              {item.name}
                                            </MenuItem>
                                          )
                                        )
                                      ) : (
                                        <MenuItem value="">
                                          {t('event:no_product_available')}
                                        </MenuItem>
                                      )}
                                    </Select>
                                  </FormControl>
                                  {selectedShowroomProduct &&
                                    selectedShowroomProduct.products[0]
                                      .type_produit === 'type_produit:WINE' && (
                                      <FormControl
                                        sx={{
                                          width: '200px',
                                        }}
                                      >
                                        <InputLabel id="simple-select-label-millesime">
                                          Millesime
                                        </InputLabel>
                                        <Select
                                          labelId="simple-select-label-millesime"
                                          id="simple-select-millesime"
                                          disabled={
                                            response?.exhibitorEvent.locked
                                          }
                                          value={selectedShowroomYear}
                                          label="Millesime"
                                          onChange={handleShowroomChange}
                                        >
                                          {selectedShowroomProduct.products?.map(
                                            (item: any, index: any) => (
                                              <MenuItem
                                                key={index}
                                                value={item.id}
                                              >
                                                {item.millesime?.replace(
                                                  'millesime:',
                                                  ''
                                                )}
                                              </MenuItem>
                                            )
                                          )}
                                        </Select>
                                      </FormControl>
                                    )}
                                </T.Flex>
                              )}
                          </T.Flex>
                        </T.BlockResponsive>
                      </A.Title>
                    )}

                    {/* <A.Alert alert="success" side="🎉" mb="15"> */}
                    {/*  {successForm && 'Form submitted successfully !'} */}
                    {/* </A.Alert> */}
                    {/* <A.Alert alert="error" side="⚠️" mb="15"> */}
                    {/*  {errorForm && <A.InnerHTML html={errorForm} />} */}
                    {/* </A.Alert> */}
                    {user?.role === 'MODERATOR' ? (
                      ''
                    ) : (
                      <>
                        <T.Flex halign="center" gap={1} wrap>
                          {response?.exhibitorEvent.locked ? (
                            ''
                          ) : (
                            <>
                              <div>
                                <A.Button
                                  type="button"
                                  color="primary"
                                  onClick={(e) => {
                                    handleParentSubmit(e).then((res) => {
                                      if (res) {
                                        handleSubmit(() =>
                                          onSubmit(getValues())
                                        )();
                                      }
                                    });
                                  }}
                                  loading={loadingForm}
                                >
                                  {t('event:save')}*
                                </A.Button>
                              </div>
                              <div>
                                <A.Button
                                  color="black"
                                  type="button"
                                  onClick={(e) => {
                                    if (products?.length === 0) {
                                      toast(
                                        t('event:add_the_least_one_product'),
                                        {
                                          type: 'error',
                                        }
                                      );
                                    }
                                    setIsSendSent(true);
                                    const missingThematic =
                                      showroomEnabled &&
                                      thematicsEnabled &&
                                      showroom_products?.some(
                                        (p) =>
                                          !getThematicValue(
                                            String(p.product_id),
                                            0
                                          )
                                      );
                                    if (missingThematic) {
                                      toast(t('event:required'), {
                                        type: 'error',
                                      });
                                      return;
                                    }
                                    // @ts-expect-error
                                    if (products?.length > 0) {
                                      handleParentSubmit(e).then((res) => {
                                        if (res) {
                                          handleSubmit(() =>
                                            onSubmit(getValues(), true)
                                          )();
                                        }
                                      });
                                    }
                                  }}
                                  bg="grey-light"
                                  iconBefore="lock-fill"
                                  loading={loadingForm}
                                >
                                  {t('event:save_and_transmit')}**
                                </A.Button>
                              </div>
                            </>
                          )}
                        </T.Flex>
                        {response?.exhibitorEvent.locked ? (
                          ''
                        ) : (
                          <>
                            <A.Text fontSize="14" textAlign="center">
                              {t(
                                'event:possibility_to_reconnect_and_sequence_the_entry_of_information'
                              )}
                            </A.Text>
                            <A.Text fontSize="14" textAlign="center">
                              {t(
                                'event:your_entry_is_finalized_the_elements_can_no_longer_be_modified'
                              )}
                            </A.Text>
                          </>
                        )}
                      </>
                    )}
                  </form>
                </T.Block>
                <CreateProductForm
                  showNewProductForm={showNewProductForm}
                  setShowNewProductForm={setShowNewProductForm}
                />
              </T.Block>
            </T.Block>
          </M.Loading>
        </T.Container>
      </div>
      {pathname.includes('product') && (
        <>
          <div className={s.ExhibitorList__outlet}>
            <div>
              <Link
                className={s.background}
                to={
                  user?.role !== 'EXHIBITOR'
                    ? generatePath(
                        routes.event.path + '/exhibitor/:exhibitorId',
                        {
                          exhibitorId,
                          eventId,
                        }
                      )
                    : generatePath(routes.eventRegistration.path, {
                        exhibitorEventId,
                        eventId,
                      })
                }
                onClick={() => setReload((e) => e + 1)}
              />
            </div>
            <div className={s.ExhibitorList__outlet__content}>
              {user?.role === 'EXHIBITOR' ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'end',
                    padding: '20px',
                  }}
                >
                  <Link
                    to={generatePath(routes.eventRegistration.path, {
                      eventId,
                      exhibitorEventId:
                        user?.role === 'EXHIBITOR'
                          ? exhibitorEventId
                          : exhibitorId,
                    })}
                    onClick={() => setReload((e) => e + 1)}
                  >
                    <div>
                      <A.Icon icon="x-lg" fontSize="24" />
                    </div>
                  </Link>
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'end',
                    padding: '20px',
                  }}
                >
                  <Link
                    to={generatePath(
                      routes.event.path + '/exhibitor/:exhibitorId',
                      {
                        eventId,
                        exhibitorId:
                          // @ts-expect-error
                          user?.role === 'EXHIBITOR'
                            ? exhibitorEventId
                            : exhibitorId,
                      }
                    )}
                    onClick={() => setReload((e) => e + 1)}
                  >
                    <div>
                      <A.Icon icon="x-lg" fontSize="24" />
                    </div>
                  </Link>
                </div>
              )}
              <Outlet />
            </div>
          </div>
        </>
      )}
      {isPopupOpen && (
        <Dialog open={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
          <DialogContent
            sx={{ padding: '32px', textAlign: 'center', minWidth: '400px' }}
          >
            <T.Block mb="20">
              <A.Title as="h2" fontSize="24" fw="bold">
                {t('event:confirmation')}
              </A.Title>
            </T.Block>

            <T.Block>
              <A.Text fontSize="16">
                Merci ! Votre enregistrement a bien été pris en compte. Un
                e-mail récapitulatif vient de vous être envoyé. Pour toute
                demande de modification, veuillez contacter l’équipe projet du
                salon.
              </A.Text>
            </T.Block>

            <T.Flex halign="center" mt="30">
              <A.Button
                onClick={() => {
                  handleRedirectEvents();
                  setIsPopupOpen(false);
                }}
                color="primary"
              >
                {t('event:ok')}
              </A.Button>
            </T.Flex>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default EventRegistration;
